import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { nakaApiBaseUrl, isLocalSnapshot } from '@/services/config';
import { withPortraitTraits } from '@/services/portrait-traits.mjs';

export default function useNakaData(cardId) {
  const nakaIds = ref({});
  const owners = ref({});
  const dispensers = ref([]);
  const orders = ref([]);
  const osListings = ref({});
  const PFPInfos = ref({});

  const initializeData = async () => {
    try {
      const [json, osinfo, pfpinfos] = await Promise.all([
        axios.get(`${nakaApiBaseUrl}/history/RAREPEPE.json`),
        axios.get(`${nakaApiBaseUrl}/os/RAREPEPE.json`),
        axios.get(`${nakaApiBaseUrl}/PFPInfos.json`),
      ]);

      parseOsListingByAddress(osinfo.data.listings);
      nakaIds.value = json.data.ids;
      owners.value = json.data.owners;
      dispensers.value = isLocalSnapshot ? {} : json.data.dispensers;
      orders.value = json.data.orders;
      PFPInfos.value = withPortraitTraits(pfpinfos.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getAddress = (naka) => {
    if (!naka || !Array.isArray(naka) || naka.length === 0) return null;
    return osListings.value[naka[naka.length - 1].address];
  };

  const getOrderStatus = (naka) => {
    const listing = getAddress(naka);
    return listing && listing.orders && listing.orders[0] && listing.orders[0].status === 'open';
  };

  const getPFPUrl = (id) => {
    if (PFPInfos.value[id] && PFPInfos.value[id].url) {
      const url = PFPInfos.value[id].url.replace('#', '%23');
      return `${nakaApiBaseUrl}/nakas/` + url;
    }
    return ''; // Return an empty string or a placeholder image URL if PFPInfo is not available
  };

  const getPrice = (naka) => {
    const listing = getAddress(naka);
    if (listing && listing.orders && listing.orders[0]) {
      return `${Math.round(listing.orders[0].getQuantity)} ${listing.orders[0].getAsset}`;
    }
    return '';
  };

  const getDispenser = (naka) => {
    if (!naka || !Array.isArray(naka) || naka.length === 0) return null;
    return dispensers.value[naka[naka.length - 1].address];
  };

  const getPriceFromDispenser = (naka) => {
    const dispenser = getDispenser(naka);
    if (dispenser) {
      return `${Math.round(dispenser.satoshirate)} BTC`;
    }
    return '';
  };

  const parseOsListingByAddress = (listings) => {
    const osListingsMap = {};
    listings.forEach((listing) => {
      osListingsMap[listing.vault] = listing;
    });
    osListings.value = osListingsMap;
  };

  const cardHistory = computed(() => (nakaIds.value['#' + cardId] || []).map((row, index) => [
    row.orderId ? 'Order' : row.dispenserId ? 'Dispenser' : index === 0 ? 'Issuance' : 'Ownership record',
    row.fromBlock,
    row.address,
  ]));

  onMounted(initializeData);

  return {
    nakaIds,
    cardHistory,
    owners,
    dispensers,
    orders,
    osListings,
    PFPInfos,
    getAddress,
    getOrderStatus,
    getPFPUrl,
    getPrice,
    getDispenser,
    getPriceFromDispenser,
  };
}
